import { useState } from "react";
import { api, downloadBlob, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Archive, FileDown, CalendarDays } from "lucide-react";

const parisToday = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
const iso = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });

// Archives & Export : export CSV des commandes par période (lecture seule, journalisé côté backend).
export default function ArchivesExport({ slug }) {
  const [from, setFrom] = useState(parisToday());
  const [to, setTo] = useState(parisToday());
  const [busy, setBusy] = useState(false);

  const preset = (kind) => {
    const now = new Date();
    if (kind === "today") { setFrom(parisToday()); setTo(parisToday()); return; }
    if (kind === "yesterday") { const d = new Date(now); d.setDate(d.getDate() - 1); setFrom(iso(d)); setTo(iso(d)); return; }
    if (kind === "week") { const d = new Date(now); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); setFrom(iso(d)); setTo(parisToday()); return; }
    if (kind === "month") { const d = new Date(now.getFullYear(), now.getMonth(), 1); setFrom(iso(d)); setTo(parisToday()); return; }
    if (kind === "prev_month") { const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); setFrom(iso(s)); setTo(iso(e)); }
  };

  const exportCsv = async () => {
    if (!from || !to) return toast.error("Choisissez une période");
    if (to < from) return toast.error("La date de fin doit être postérieure à la date de début");
    setBusy(true);
    try {
      const blob = await api.ordersExportCsv(slug, { from, to });
      downloadBlob(blob, `commandes_${slug}_${from}_${to}.csv`);
      toast.success("Export CSV généré — enregistrez-le sur votre clé USB ou disque externe");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="archives-export">
      <div>
        <h2 className="font-serif-display text-2xl flex items-center gap-2"><Archive className="w-5 h-5 text-[#C69C4A]" /> Archives & Export</h2>
        <p className="text-xs text-stone-400 mt-0.5">Exportez vos ventes (commandes, détail des plats, montants, modes de paiement) au format CSV UTF-8 compatible Excel. L'export est en lecture seule et journalisé.</p>
      </div>

      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
        <div className="flex flex-wrap gap-2">
          {[["today", "Aujourd'hui"], ["yesterday", "Hier"], ["week", "Cette semaine"], ["month", "Ce mois"], ["prev_month", "Mois précédent"]].map(([k, l]) => (
            <button key={k} data-testid={`export-preset-${k}`} onClick={() => preset(k)} className="rounded-full px-3.5 py-1.5 text-xs font-semibold bg-[#0A1712] border border-[#1B3028] text-stone-300 hover:border-[#C69C4A]/60 transition-colors">{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date de début</label>
            <Input data-testid="export-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-10 mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date de fin</label>
            <Input data-testid="export-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-10 mt-1" />
          </div>
          <Button data-testid="export-csv-btn" onClick={exportCsv} disabled={busy} className="rounded-full bg-[#C69C4A] text-[#0C1713] hover:bg-[#B38B3A] font-bold h-10 px-5">
            <FileDown className="w-4 h-4 mr-2" /> {busy ? "Génération…" : "Exporter au format CSV"}
          </Button>
        </div>
        <p className="text-[11px] text-stone-500">Colonnes : ID Commande · Date et Heure · Numéro de Table · Nom du Client · Détail des Plats · Montant Total (€) · Mode de Paiement · Statut · Source. Séparateur « ; », encodage UTF-8 avec BOM (ouverture directe dans Excel sans caractères illisibles).</p>
      </div>
    </div>
  );
}
